import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders, MARKETING_API_BASE_URL, getMarketingApiHeaders } from '../config/api';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

// ── Types ──────────────────────────────────────────────────────────────────

interface AnnouncementDetail {
  _id: string;
  title: string;
  status: string;
  posterImageUrl?: string | null;
  storyboardVideo?: {
    status: 'processing' | 'ready' | 'failed';
    sourceUrl: string;
    resolutions: string[];
    error?: string | null;
  } | null;
  eventConfig?: {
    categoryKey: string;
    milestones?: { value: number; label: string }[];
    badge?: { name: string; description?: string };
  } | null;
}

interface WalletProduct {
  ProductID: string;
  ProductKey: string;
  Name: string;
  Type: string;
  CategoryKey: string;
  IsActive: boolean;
  DisplayOrder: number;
  AvailabilityType?: string;
  PriceCoins?: number;
  PriceTokens?: number;
  MediaAssets: { Type: string; Url: string; MimeType: string }[];
  Metadata?: Record<string, unknown>;
}

// Fields required by the wallet create schema validation (updateWalletShopProduct
// validates through createWalletShopProduct, so all required create fields must be sent)
function requiredProductFields(product: WalletProduct): Record<string, unknown> {
  return {
    Name: product.Name,
    CategoryKey: product.CategoryKey,
    Type: product.Type,
    AvailabilityType: product.AvailabilityType || 'unlimited',
    PriceCoins: product.PriceCoins ?? 0,
  };
}

type AssetTab = 'thumbnail' | 'badge' | 'milestone' | 'gift' | 'logo' | 'background' | 'collection_bg';

// ── Lottie preview ──────────────────────────────────────────────────────────

const loadLottie = (): Promise<any> =>
  new Promise((resolve) => {
    const win = window as any;
    if (win.lottie) return resolve(win.lottie);
    const existing = document.querySelector('script[data-lottie]');
    if (existing) {
      const interval = setInterval(() => {
        if (win.lottie) { clearInterval(interval); resolve(win.lottie); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.setAttribute('data-lottie', '1');
    script.onload = () => resolve(win.lottie);
    document.head.appendChild(script);
  });

const LottiePreview: React.FC<{ data: any }> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);
  useEffect(() => {
    let cancelled = false;
    loadLottie().then((lottie) => {
      if (cancelled || !containerRef.current) return;
      animRef.current?.destroy();
      animRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: data,
      });
    });
    return () => { cancelled = true; animRef.current?.destroy(); };
  }, [data]);
  return <div ref={containerRef} style={{ width: 100, height: 100 }} />;
};

// ── Upload helpers ──────────────────────────────────────────────────────────

async function presignAndUpload(
  productKey: string,
  assetType: string,
  file: File,
  contentType: string,
  headers: Record<string, string>,
): Promise<string> {
  const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ProductKey: productKey, ContentType: contentType, AssetType: assetType }),
  });
  const presignedJson = await presignedRes.json();
  if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');
  const { UploadUrl, AssetUrl } = presignedJson.data;
  const uploadRes = await fetch(UploadUrl, {
    method: 'PUT',
    body: await file.arrayBuffer(),
    headers: { 'Content-Type': contentType },
  });
  if (!uploadRes.ok) throw new Error('Failed to upload file to storage');
  return AssetUrl as string;
}

// ── Lottie JSON reader (module-level) ───────────────────────────────────────

function readLottie(file: File): Promise<any> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try { resolve(JSON.parse(e.target?.result as string)); }
      catch { reject(new Error('Invalid Lottie JSON')); }
    };
    reader.readAsText(file);
  });
}

// ── Asset forms ─────────────────────────────────────────────────────────────

interface ImageFormProps {
  categoryKey: string;
  productType: string;
  label: string;
  placeholderKey: string;
  onCreated: (assetUrl?: string) => void;
}

const ImageAssetForm: React.FC<ImageFormProps> = ({ categoryKey, productType, label, placeholderKey, onCreated }) => {
  const [name, setName] = useState('');
  const [productKey, setProductKey] = useState('');
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    if (!productKey) {
      setProductKey(f.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]+/g, '_').toUpperCase());
      setName(f.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim());
    }
  };

  const handleSubmit = async () => {
    if (!file || !productKey.trim() || !name.trim()) {
      toast.error('Name, product key, and image file are required.');
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    const headers = getApiHeaders(token);
    setSubmitting(true);
    try {
      const contentType = file.type || 'image/png';
      const imageUrl = await presignAndUpload(productKey.trim(), 'image', file, contentType, headers);
      const productRes = await fetch(`${walletApiBase}/createWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: productKey.trim(),
          Name: name.trim(),
          Description: description.trim() || `${label} for ${categoryKey}.`,
          CategoryKey: categoryKey,
          Type: productType,
          CurrencyType: 'token',
          PriceCoins: 0,
          PriceTokens: 0,
          AvailabilityType: 'unlimited',
          DisplayOrder: Number(displayOrder) || 1,
          IsActive: true,
          MediaAssets: [{ Type: 'image', Url: imageUrl, MimeType: contentType }],
        }),
      });
      const productJson = await productRes.json();
      if (!productRes.ok) throw new Error(productJson.message || 'Failed to create product');
      toast.success(`${label} created!`);
      setName(''); setProductKey(''); setDescription(''); setFile(null); setPreviewUrl(null); setDisplayOrder('1');
      onCreated(imageUrl);
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='space-y-3'>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} placeholder={label}
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm disabled:opacity-50' />
        </div>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Product Key *</label>
          <input value={productKey} onChange={(e) => setProductKey(e.target.value.replace(/\s+/g, '_').toUpperCase())}
            disabled={submitting} placeholder={placeholderKey}
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white font-mono text-sm placeholder-gray-500 disabled:opacity-50' />
        </div>
        <div className='sm:col-span-2'>
          <label className='block text-xs text-gray-400 mb-1'>Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} disabled={submitting} placeholder='Optional description'
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm disabled:opacity-50' />
        </div>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Display Order</label>
          <input type='number' min='0' value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} disabled={submitting}
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm disabled:opacity-50' />
        </div>
      </div>

      <div className='flex items-center gap-4'>
        <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
          {file ? 'Replace image' : 'Select image'}
          <input type='file' accept='image/png,image/jpeg,image/webp' className='hidden' onChange={handleFile} disabled={submitting} />
        </label>
        {file && <span className='text-xs text-gray-400 truncate max-w-[200px]'>{file.name}</span>}
      </div>

      {previewUrl && (
        <img src={previewUrl} alt='preview' className='h-20 w-auto rounded-lg border border-white/10 object-contain bg-black/20' />
      )}

      <button onClick={handleSubmit} disabled={!file || !productKey.trim() || !name.trim() || submitting}
        className='px-5 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'>
        {submitting ? 'Uploading…' : `Create ${label}`}
      </button>
    </div>
  );
};

// Rarity config
const RARITY_TIERS = [
  { value: 'mythic', label: 'Mythic', color: '#FF5F9E' },
  { value: 'legendary', label: 'Legendary', color: '#FFB700' },
  { value: 'epic', label: 'Epic', color: '#9B59FF' },
  { value: 'rare', label: 'Rare', color: '#4FC3F7' },
  { value: 'normal', label: 'Normal', color: '#8E8E93' },
];

// Gift form (Lottie + Rarity)
interface GiftFormProps {
  categoryKey: string;
  onCreated: (assetUrl?: string) => void;
}

const GiftAssetForm: React.FC<GiftFormProps> = ({ categoryKey, onCreated }) => {
  const [name, setName] = useState('');
  const [productKey, setProductKey] = useState('');
  const [tier, setTier] = useState<string>('rare');
  const [priceTokens, setPriceTokens] = useState('50');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [giftFile, setGiftFile] = useState<File | null>(null);
  const [giftData, setGiftData] = useState<any | null>(null);
  const [bgFile, setBgFile] = useState<File | null>(null);
  const [bgData, setBgData] = useState<any | null>(null);
  const [rarityFile, setRarityFile] = useState<File | null>(null);
  const [rarityPreviewUrl, setRarityPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleGiftFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    try {
      const data = await readLottie(f);
      setGiftFile(f); setGiftData(data);
      if (!productKey) {
        setProductKey(f.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]+/g, '_').toUpperCase());
        setName(f.name.replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim());
      }
    } catch { toast.error('Invalid Lottie JSON'); }
  };

  const handleBgFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    try {
      const data = await readLottie(f);
      setBgFile(f); setBgData(data);
    } catch { toast.error('Invalid Lottie JSON'); }
  };

  const handleRarityFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setRarityFile(f);
    setRarityPreviewUrl(URL.createObjectURL(f));
  };

  const handleSubmit = async () => {
    if (!giftFile || !productKey.trim() || !name.trim()) {
      toast.error('Gift animation, product key, and name are required.');
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    const headers = getApiHeaders(token);
    setSubmitting(true);
    try {
      const giftUrl = await presignAndUpload(productKey.trim(), 'animation', giftFile, 'application/json', headers);
      const mediaAssets: { Type: string; Url: string; MimeType: string }[] = [
        { Type: 'animation', Url: giftUrl, MimeType: 'application/json' },
      ];
      if (bgFile) {
        const bgUrl = await presignAndUpload(productKey.trim(), 'background', bgFile, 'application/json', headers);
        mediaAssets.push({ Type: 'background', Url: bgUrl, MimeType: 'application/json' });
      }
      if (rarityFile) {
        const rarityContentType = rarityFile.type || 'image/png';
        const rarityUrl = await presignAndUpload(productKey.trim(), 'rarity', rarityFile, rarityContentType, headers);
        mediaAssets.push({ Type: 'rarity', Url: rarityUrl, MimeType: rarityContentType });
      }
      const productRes = await fetch(`${walletApiBase}/createWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: productKey.trim(),
          Name: name.trim(),
          CategoryKey: categoryKey,
          Type: 'event_gift',
          Tags: [tier],
          CurrencyType: 'token',
          PriceCoins: 0,
          PriceTokens: Number(priceTokens) || 50,
          AvailabilityType: 'unlimited',
          DisplayOrder: Number(displayOrder) || 1,
          IsActive: true,
          MediaAssets: mediaAssets,
        }),
      });
      const productJson = await productRes.json();
      if (!productRes.ok) throw new Error(productJson.message || 'Failed to create product');
      toast.success(`Event gift "${name}" created!`);
      setName(''); setProductKey(''); setTier('rare'); setGiftFile(null); setGiftData(null);
      setBgFile(null); setBgData(null); setRarityFile(null); setRarityPreviewUrl(null);
      setPriceTokens('50'); setDisplayOrder('1');
      onCreated();
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const activeTierColor = RARITY_TIERS.find(t => t.value === tier)?.color || '#4FC3F7';

  return (
    <div className='space-y-3'>
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} disabled={submitting} placeholder='Golden Throne'
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm disabled:opacity-50' />
        </div>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Product Key *</label>
          <input value={productKey} onChange={(e) => setProductKey(e.target.value.replace(/\s+/g, '_').toUpperCase())}
            disabled={submitting} placeholder='GOLDEN_THRONE'
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white font-mono text-sm placeholder-gray-500 disabled:opacity-50' />
        </div>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Rarity Tier</label>
          <div className='flex gap-2 flex-wrap'>
            {RARITY_TIERS.map((t) => (
              <button
                key={t.value}
                type='button'
                disabled={submitting}
                onClick={() => setTier(t.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition disabled:opacity-50 ${
                  tier === t.value
                    ? 'text-black'
                    : 'bg-white/5 border-white/20 text-gray-400 hover:border-white/40'
                }`}
                style={tier === t.value ? { backgroundColor: t.color, borderColor: t.color } : {}}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Price (Tokens)</label>
          <input type='number' min='1' value={priceTokens} onChange={(e) => setPriceTokens(e.target.value)} disabled={submitting}
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm disabled:opacity-50' />
        </div>
        <div>
          <label className='block text-xs text-gray-400 mb-1'>Display Order</label>
          <input type='number' min='0' value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} disabled={submitting}
            className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm disabled:opacity-50' />
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
        <div>
          <p className='text-xs text-gray-400 mb-2'>Gift animation (Lottie JSON) <span className='text-[#F42026]'>*</span></p>
          <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
            {giftFile ? 'Replace' : 'Select .json'}
            <input type='file' accept='.json' className='hidden' onChange={handleGiftFile} disabled={submitting} />
          </label>
          {giftData && (
            <div className='mt-2 rounded-lg overflow-hidden bg-black/30 border border-white/10 flex items-center justify-center' style={{ height: 120 }}>
              <LottiePreview data={giftData} />
            </div>
          )}
        </div>
        <div>
          <p className='text-xs text-gray-400 mb-2'>Background animation (optional)</p>
          <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
            {bgFile ? 'Replace' : 'Select .json'}
            <input type='file' accept='.json' className='hidden' onChange={handleBgFile} disabled={submitting} />
          </label>
          {bgData && (
            <div className='mt-2 rounded-lg overflow-hidden bg-black/30 border border-white/10 flex items-center justify-center' style={{ height: 120 }}>
              <LottiePreview data={bgData} />
            </div>
          )}
        </div>
        <div>
          <p className='text-xs text-gray-400 mb-2'>
            Rarity badge image
            <span className='ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold' style={{ backgroundColor: `${activeTierColor}33`, color: activeTierColor }}>
              {RARITY_TIERS.find(t => t.value === tier)?.label}
            </span>
          </p>
          <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
            {rarityFile ? 'Replace' : 'Select image'}
            <input type='file' accept='image/png,image/jpeg,image/webp,image/gif' className='hidden' onChange={handleRarityFile} disabled={submitting} />
          </label>
          {rarityPreviewUrl && (
            <img src={rarityPreviewUrl} alt='rarity badge preview' className='mt-2 h-12 w-auto object-contain rounded border border-white/10' />
          )}
        </div>
      </div>

      <button onClick={handleSubmit} disabled={!giftFile || !productKey.trim() || !name.trim() || submitting}
        className='px-5 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'>
        {submitting ? 'Creating…' : 'Create Event Gift'}
      </button>
    </div>
  );
};

// ── Milestone reward uploads ─────────────────────────────────────────────────

type RewardType = 'profile_frame' | 'chat_theme' | 'wallpaper' | 'opening_anim' | 'badge';

const REWARD_TYPES: { value: RewardType; label: string }[] = [
  { value: 'profile_frame', label: 'Profile Frame' },
  { value: 'chat_theme', label: 'Chat Theme (6 assets)' },
  { value: 'wallpaper', label: 'Profile Wallpaper' },
  { value: 'opening_anim', label: 'Opening Animation (Lottie)' },
  { value: 'badge', label: 'Exclusive Badge (PNG)' },
];

function detectRewardType(name: string): RewardType {
  const n = name.toLowerCase();
  if (n.includes('profile frame') || n.includes('frame')) return 'profile_frame';
  if (n.includes('chat theme') || n.includes('chat')) return 'chat_theme';
  if (n.includes('wallpaper')) return 'wallpaper';
  if (n.includes('opening anim') || n.includes('opening animation')) return 'opening_anim';
  if (n.includes('badge')) return 'badge';
  return 'profile_frame';
}

const MilestoneRewardSection: React.FC<{
  product: WalletProduct;
  onSaved: () => void;
  disabled: boolean;
}> = ({ product, onSaved, disabled }) => {
  const [rewardType, setRewardType] = useState<RewardType>(() => detectRewardType(product.Name));
  const [submitting, setSubmitting] = useState(false);

  // Card image — the thumbnail shown on the milestone tracker (MediaAssets[0])
  const [cardFile, setCardFile] = useState<File | null>(null);
  const [cardPreview, setCardPreview] = useState<string | null>(null);
  const existingCardUrl = product.MediaAssets?.find(m => m.Type === 'image')?.Url;

  // Single image (profile_frame / wallpaper / badge)
  const [singleFile, setSingleFile] = useState<File | null>(null);
  const [singlePreview, setSinglePreview] = useState<string | null>(null);

  // Opening animation (Lottie, legacy) + webm/mov (preferred — plays as real video)
  const [animFile, setAnimFile] = useState<File | null>(null);
  const [animData, setAnimData] = useState<any | null>(null);
  const [animWebmFile, setAnimWebmFile] = useState<File | null>(null);
  const [animMovFile, setAnimMovFile] = useState<File | null>(null);

  // Chat theme — 4 images + 2 colors
  const [chatLightFile, setChatLightFile] = useState<File | null>(null);
  const [chatLightPreview, setChatLightPreview] = useState<string | null>(null);
  const [chatThumbLightFile, setChatThumbLightFile] = useState<File | null>(null);
  const [chatThumbLightPreview, setChatThumbLightPreview] = useState<string | null>(null);
  const [chatDarkFile, setChatDarkFile] = useState<File | null>(null);
  const [chatDarkPreview, setChatDarkPreview] = useState<string | null>(null);
  const [chatThumbDarkFile, setChatThumbDarkFile] = useState<File | null>(null);
  const [chatThumbDarkPreview, setChatThumbDarkPreview] = useState<string | null>(null);
  const [bubbleLightColor, setBubbleLightColor] = useState('#ffffff');
  const [bubbleDarkColor, setBubbleDarkColor] = useState('#000000');

  // Milestone name
  const [milestoneName, setMilestoneName] = useState<string>(product.Name || '');
  const savedMilestoneName = product.Name || '';

  // Token spend threshold — stored directly as PriceCoins on the product
  const [priceCoins, setPriceCoins] = useState<number>(product.PriceCoins ?? 0);
  const savedPriceCoins = product.PriceCoins ?? 0;

  // CTA labels stored in Metadata.ctaLabel / Metadata.seenCtaLabel
  const [ctaLabel, setCtaLabel] = useState<string>((product.Metadata?.ctaLabel as string) || '');
  const [seenCtaLabel, setSeenCtaLabel] = useState<string>((product.Metadata?.seenCtaLabel as string) || '');
  const savedCtaLabel = (product.Metadata?.ctaLabel as string) || '';
  const savedSeenCtaLabel = (product.Metadata?.seenCtaLabel as string) || '';
  const hasLabelChanges = milestoneName !== savedMilestoneName || ctaLabel !== savedCtaLabel || seenCtaLabel !== savedSeenCtaLabel || priceCoins !== savedPriceCoins;

  // Reward data is stored in Metadata.rewards (free-form object, no MediaAssets enum constraints)
  const savedRewards = (product.Metadata?.rewards || {}) as Record<string, string>;
  const ex = {
    profile_frame: savedRewards.profile_frame ? { Url: savedRewards.profile_frame } : undefined,
    wallpaper: savedRewards.wallpaper ? { Url: savedRewards.wallpaper } : undefined,
    badge: savedRewards.badge ? { Url: savedRewards.badge } : undefined,
    opening_anim: savedRewards.opening_anim ? { Url: savedRewards.opening_anim } : undefined,
    opening_anim_webm: savedRewards.opening_anim_webm ? { Url: savedRewards.opening_anim_webm } : undefined,
    opening_anim_mov: savedRewards.opening_anim_mov ? { Url: savedRewards.opening_anim_mov } : undefined,
    chat_light: savedRewards.chat_light ? { Url: savedRewards.chat_light } : undefined,
    chat_thumb_light: savedRewards.chat_thumb_light ? { Url: savedRewards.chat_thumb_light } : undefined,
    chat_dark: savedRewards.chat_dark ? { Url: savedRewards.chat_dark } : undefined,
    chat_thumb_dark: savedRewards.chat_thumb_dark ? { Url: savedRewards.chat_thumb_dark } : undefined,
    chat_bubble_light: savedRewards.chat_bubble_light ? { Url: savedRewards.chat_bubble_light } : undefined,
    chat_bubble_dark: savedRewards.chat_bubble_dark ? { Url: savedRewards.chat_bubble_dark } : undefined,
  };

  const isFirstTimeChat = rewardType === 'chat_theme' && !ex.chat_light;

  const hasNewFile = cardFile || singleFile || animFile || animWebmFile || animMovFile || chatLightFile || chatThumbLightFile || chatDarkFile || chatThumbDarkFile;
  const chatFirstTimeReady = chatLightFile && chatThumbLightFile && chatDarkFile && chatThumbDarkFile;
  const canSubmit = !submitting && !disabled && (hasLabelChanges || hasNewFile) && (
    !hasNewFile || rewardType !== 'chat_theme' || !isFirstTimeChat || !!chatFirstTimeReady
  );

  const resetAll = () => {
    setCardFile(null); setCardPreview(null);
    setSingleFile(null); setSinglePreview(null);
    setAnimFile(null); setAnimData(null);
    setAnimWebmFile(null); setAnimMovFile(null);
    setChatLightFile(null); setChatLightPreview(null);
    setChatThumbLightFile(null); setChatThumbLightPreview(null);
    setChatDarkFile(null); setChatDarkPreview(null);
    setChatThumbDarkFile(null); setChatThumbDarkPreview(null);
  };

  const handleTypeChange = (t: RewardType) => { setRewardType(t); resetAll(); };

  const imgPicker = (
    label: string,
    file: File | null,
    preview: string | null,
    existing: { Url: string } | undefined,
    onChange: (f: File) => void,
    required = false,
  ) => (
    <div>
      <p className='text-[10px] text-gray-400 mb-1'>{label}{required && <span className='text-red-400 ml-0.5'>*</span>}</p>
      {existing && !file && <img src={existing.Url} alt={label} className='h-10 w-auto rounded border border-white/10 object-contain mb-1' />}
      <label className={`cursor-pointer px-2 py-1 text-[10px] font-semibold rounded bg-white/10 text-white hover:bg-white/20 transition inline-block ${submitting || disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        {file ? file.name.slice(0, 18) : existing ? 'Replace' : 'Select'}
        <input type='file' accept='image/png,image/jpeg,image/webp' className='hidden' disabled={submitting || disabled} onChange={e => {
          const f = e.target.files?.[0]; e.target.value = '';
          if (f) onChange(f);
        }} />
      </label>
      {preview && <img src={preview} alt='preview' className='mt-1 h-10 w-auto rounded border border-white/10 object-contain' />}
    </div>
  );

  const handleSubmit = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    const headers = getApiHeaders(token);
    setSubmitting(true);
    try {
      // Start with existing saved rewards and merge in new values
      const rewards: Record<string, string> = { ...savedRewards };

      if (rewardType === 'profile_frame' && singleFile) {
        rewards.profile_frame = await presignAndUpload(product.ProductKey, 'reward_profile_frame', singleFile, singleFile.type || 'image/png', headers);
      } else if (rewardType === 'wallpaper' && singleFile) {
        rewards.wallpaper = await presignAndUpload(product.ProductKey, 'reward_wallpaper', singleFile, singleFile.type || 'image/png', headers);
      } else if (rewardType === 'badge' && singleFile) {
        rewards.badge = await presignAndUpload(product.ProductKey, 'reward_badge', singleFile, 'image/png', headers);
      } else if (rewardType === 'opening_anim' && (animFile || animWebmFile || animMovFile)) {
        if (animFile) {
          rewards.opening_anim = await presignAndUpload(product.ProductKey, 'reward_opening_anim', animFile, 'application/json', headers);
        }
        if (animWebmFile) {
          rewards.opening_anim_webm = await presignAndUpload(product.ProductKey, 'reward_opening_anim_webm', animWebmFile, 'video/webm', headers);
        }
        if (animMovFile) {
          const movContentType = animMovFile.type || 'video/quicktime';
          rewards.opening_anim_mov = await presignAndUpload(product.ProductKey, 'reward_opening_anim_mov', animMovFile, movContentType, headers);
        }
      } else if (rewardType === 'chat_theme') {
        if (isFirstTimeChat && !chatFirstTimeReady) {
          toast.error('All 4 images required for first upload.'); return;
        }
        if (chatLightFile) rewards.chat_light = await presignAndUpload(product.ProductKey, 'reward_chat_light', chatLightFile, chatLightFile.type || 'image/png', headers);
        if (chatThumbLightFile) rewards.chat_thumb_light = await presignAndUpload(product.ProductKey, 'reward_chat_thumb_light', chatThumbLightFile, chatThumbLightFile.type || 'image/png', headers);
        if (chatDarkFile) rewards.chat_dark = await presignAndUpload(product.ProductKey, 'reward_chat_dark', chatDarkFile, chatDarkFile.type || 'image/png', headers);
        if (chatThumbDarkFile) rewards.chat_thumb_dark = await presignAndUpload(product.ProductKey, 'reward_chat_thumb_dark', chatThumbDarkFile, chatThumbDarkFile.type || 'image/png', headers);
        rewards.chat_bubble_light = bubbleLightColor;
        rewards.chat_bubble_dark = bubbleDarkColor;
      }

      // Card image (MediaAssets[0]) — the thumbnail shown on the milestone tracker
      let updatedMediaAssets = product.MediaAssets || [];
      if (cardFile) {
        const cardContentType = cardFile.type || 'image/png';
        const cardUrl = await presignAndUpload(product.ProductKey, 'image', cardFile, cardContentType, headers);
        updatedMediaAssets = [{ Type: 'image', Url: cardUrl, MimeType: cardContentType }];
      }

      // Rewards stored in Metadata.rewards (free-form object — avoids MediaAssets type enum constraints)
      const updatedMetadata = {
        ...(product.Metadata || {}),
        rewards,
        ctaLabel: ctaLabel.trim(),
        seenCtaLabel: seenCtaLabel.trim(),
      };

      const res = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: product.ProductKey,
          ...requiredProductFields(product),
          Name: milestoneName.trim() || product.Name,
          PriceCoins: priceCoins,
          Metadata: updatedMetadata,
          MediaAssets: updatedMediaAssets,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Update failed');
      toast.success('Reward assets saved!');
      resetAll();
      onSaved();
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='bg-[#FF9C15]/5 border border-[#FF9C15]/20 rounded-lg p-3 space-y-3 mt-2'>
      {/* Card image — shown on the milestone tracker */}
      <div className='border-b border-white/10 pb-3 space-y-2'>
        <p className='text-[10px] font-semibold text-[#FF9C15]'>Card Image</p>
        <p className='text-[10px] text-gray-400'>Thumbnail shown on the milestone tracker bar</p>
        {existingCardUrl && !cardFile && (
          <img src={existingCardUrl} alt='card' className='h-14 w-auto rounded border border-white/10 object-contain' />
        )}
        <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block ${submitting || disabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {cardFile ? cardFile.name.slice(0, 20) : existingCardUrl ? 'Replace' : 'Select image'}
          <input type='file' accept='image/png,image/jpeg,image/webp' className='hidden' disabled={submitting || disabled} onChange={e => {
            const f = e.target.files?.[0]; e.target.value = '';
            if (!f) return; setCardFile(f); setCardPreview(URL.createObjectURL(f));
          }} />
        </label>
        {cardPreview && <img src={cardPreview} alt='preview' className='mt-1 h-14 w-auto rounded border border-white/10 object-contain' />}
      </div>

      <div className='flex items-center justify-between gap-2'>
        <p className='text-[10px] font-semibold text-[#FF9C15] shrink-0'>Reward Asset</p>
        <select
          value={rewardType}
          onChange={e => handleTypeChange(e.target.value as RewardType)}
          disabled={submitting || disabled}
          className='text-[10px] bg-white/10 border border-white/20 rounded px-2 py-1 text-white disabled:opacity-50 flex-1 min-w-0'
        >
          {REWARD_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
        </select>
      </div>

      {/* Single image upload */}
      {(rewardType === 'profile_frame' || rewardType === 'wallpaper' || rewardType === 'badge') && (
        <div>
          {!singleFile && ex[rewardType === 'profile_frame' ? 'profile_frame' : rewardType === 'wallpaper' ? 'wallpaper' : 'badge'] && (
            <img
              src={ex[rewardType === 'profile_frame' ? 'profile_frame' : rewardType === 'wallpaper' ? 'wallpaper' : 'badge']!.Url}
              alt='current' className='h-14 w-auto rounded border border-white/10 object-contain mb-2'
            />
          )}
          <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block ${submitting || disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {singleFile ? 'Replace' : rewardType === 'badge' ? 'Select PNG' : 'Select image'}
            <input type='file' accept={rewardType === 'badge' ? 'image/png' : 'image/png,image/jpeg,image/webp'} className='hidden' disabled={submitting || disabled} onChange={e => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (!f) return; setSingleFile(f); setSinglePreview(URL.createObjectURL(f));
            }} />
          </label>
          {singlePreview && <img src={singlePreview} alt='preview' className='mt-2 h-14 w-auto rounded border border-white/10 object-contain' />}
        </div>
      )}

      {/* Opening animation — webm/mov (plays as real video, preferred) + legacy Lottie fallback */}
      {rewardType === 'opening_anim' && (
        <div className='space-y-3'>
          <div className='grid grid-cols-2 gap-2'>
            <div>
              <p className='text-[10px] text-gray-400 mb-1'>WebM (Android)</p>
              {ex.opening_anim_webm && !animWebmFile && (
                <p className='text-[10px] text-emerald-400 mb-1'>✓ {ex.opening_anim_webm.Url.split('/').pop()}</p>
              )}
              <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block ${submitting || disabled ? 'opacity-50 pointer-events-none' : ''}`}>
                {animWebmFile ? animWebmFile.name.slice(0, 18) : ex.opening_anim_webm ? 'Replace .webm' : 'Select .webm'}
                <input type='file' accept='video/webm,.webm' className='hidden' disabled={submitting || disabled} onChange={e => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (f) setAnimWebmFile(f);
                }} />
              </label>
              {animWebmFile && <p className='text-[10px] text-emerald-400 mt-1'>{animWebmFile.name}</p>}
            </div>
            <div>
              <p className='text-[10px] text-gray-400 mb-1'>iOS alpha (.mov/.mp4)</p>
              {ex.opening_anim_mov && !animMovFile && (
                <p className='text-[10px] text-emerald-400 mb-1'>✓ {ex.opening_anim_mov.Url.split('/').pop()}</p>
              )}
              <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block ${submitting || disabled ? 'opacity-50 pointer-events-none' : ''}`}>
                {animMovFile ? animMovFile.name.slice(0, 18) : ex.opening_anim_mov ? 'Replace .mov' : 'Select .mov'}
                <input type='file' accept='video/quicktime,video/mp4,video/x-m4v,.mov,.mp4,.m4v' className='hidden' disabled={submitting || disabled} onChange={e => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (f) setAnimMovFile(f);
                }} />
              </label>
              {animMovFile && <p className='text-[10px] text-emerald-400 mt-1'>{animMovFile.name}</p>}
            </div>
          </div>

          <div>
            <p className='text-[10px] text-gray-400 mb-1'>Legacy Lottie (.json) — optional fallback for old app versions</p>
            {ex.opening_anim && !animFile && (
              <p className='text-[10px] text-emerald-400 mb-1'>✓ {ex.opening_anim.Url.split('/').pop()}</p>
            )}
            <label className={`cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block ${submitting || disabled ? 'opacity-50 pointer-events-none' : ''}`}>
              {animFile ? animFile.name.slice(0, 20) : ex.opening_anim ? 'Replace .json' : 'Select .json'}
              <input type='file' accept='.json' className='hidden' disabled={submitting || disabled} onChange={async e => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                try { const data = await readLottie(f); setAnimFile(f); setAnimData(data); }
                catch { toast.error('Invalid Lottie JSON'); }
              }} />
            </label>
            {animData && (
              <div className='mt-2 rounded-lg overflow-hidden bg-black/30 border border-white/10 flex items-center justify-center' style={{ height: 80 }}>
                <LottiePreview data={animData} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Theme — 4 images + 2 colors */}
      {rewardType === 'chat_theme' && (
        <div className='space-y-3'>
          {isFirstTimeChat && (
            <p className='text-[10px] text-yellow-400'>First upload requires all 4 images. Colors optional.</p>
          )}
          <div className='grid grid-cols-2 gap-2'>
            {imgPicker('Theme Light Mode', chatLightFile, chatLightPreview, ex.chat_light, f => { setChatLightFile(f); setChatLightPreview(URL.createObjectURL(f)); }, isFirstTimeChat)}
            {imgPicker('Thumbnail Light', chatThumbLightFile, chatThumbLightPreview, ex.chat_thumb_light, f => { setChatThumbLightFile(f); setChatThumbLightPreview(URL.createObjectURL(f)); }, isFirstTimeChat)}
            {imgPicker('Theme Dark Mode', chatDarkFile, chatDarkPreview, ex.chat_dark, f => { setChatDarkFile(f); setChatDarkPreview(URL.createObjectURL(f)); }, isFirstTimeChat)}
            {imgPicker('Thumbnail Dark', chatThumbDarkFile, chatThumbDarkPreview, ex.chat_thumb_dark, f => { setChatThumbDarkFile(f); setChatThumbDarkPreview(URL.createObjectURL(f)); }, isFirstTimeChat)}
          </div>
          <div className='grid grid-cols-2 gap-2'>
            <div>
              <p className='text-[10px] text-gray-400 mb-1'>Bubble Color — Light</p>
              <div className='flex items-center gap-2'>
                <input type='color' value={bubbleLightColor} onChange={e => setBubbleLightColor(e.target.value)} disabled={submitting || disabled}
                  className='h-7 w-10 rounded border border-white/20 bg-transparent cursor-pointer disabled:opacity-50' />
                <span className='text-[10px] font-mono text-gray-400'>{bubbleLightColor}</span>
              </div>
            </div>
            <div>
              <p className='text-[10px] text-gray-400 mb-1'>Bubble Color — Dark</p>
              <div className='flex items-center gap-2'>
                <input type='color' value={bubbleDarkColor} onChange={e => setBubbleDarkColor(e.target.value)} disabled={submitting || disabled}
                  className='h-7 w-10 rounded border border-white/20 bg-transparent cursor-pointer disabled:opacity-50' />
                <span className='text-[10px] font-mono text-gray-400'>{bubbleDarkColor}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestone name */}
      <div className='border-t border-white/10 pt-3 space-y-2'>
        <p className='text-[10px] font-semibold text-[#FF9C15]'>Milestone Name</p>
        <input
          type='text'
          value={milestoneName}
          onChange={e => setMilestoneName(e.target.value)}
          disabled={submitting || disabled}
          placeholder='e.g. 10K Reward — Profile Frame'
          className='w-full text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white placeholder-gray-500 disabled:opacity-50'
        />
      </div>

      {/* Token spend threshold */}
      <div className='border-t border-white/10 pt-3 space-y-2'>
        <p className='text-[10px] font-semibold text-[#FF9C15]'>Unlock Threshold</p>
        <div>
          <p className='text-[10px] text-gray-400 mb-1'>Token spend required to unlock this milestone (e.g. 10000)</p>
          <input
            type='number'
            min='0'
            value={priceCoins}
            onChange={e => setPriceCoins(Number(e.target.value) || 0)}
            disabled={submitting || disabled}
            placeholder='10000'
            className='w-full text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white placeholder-gray-500 disabled:opacity-50'
          />
        </div>
      </div>

      {/* CTA labels for milestone_unlock popup */}
      <div className='border-t border-white/10 pt-3 space-y-2'>
        <p className='text-[10px] font-semibold text-[#FF9C15]'>Popup CTA Labels</p>
        <div>
          <p className='text-[10px] text-gray-400 mb-1'>First-time label (e.g. "Claim Reward")</p>
          <input
            type='text'
            value={ctaLabel}
            onChange={e => setCtaLabel(e.target.value)}
            disabled={submitting || disabled}
            placeholder='Claim Reward'
            className='w-full text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white placeholder-gray-500 disabled:opacity-50'
          />
        </div>
        <div>
          <p className='text-[10px] text-gray-400 mb-1'>After-seen label (e.g. "View Reward")</p>
          <input
            type='text'
            value={seenCtaLabel}
            onChange={e => setSeenCtaLabel(e.target.value)}
            disabled={submitting || disabled}
            placeholder='View Reward'
            className='w-full text-xs bg-white/10 border border-white/20 rounded px-2 py-1.5 text-white placeholder-gray-500 disabled:opacity-50'
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        className='px-4 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
      >
        {submitting ? 'Uploading…' : 'Save Reward Assets'}
      </button>
    </div>
  );
};

// ── Existing assets list ────────────────────────────────────────────────────

const typeLabel: Record<string, string> = {
  event_thumbnail: 'Thumbnail',
  event_badge: 'Badge',
  event_milestone: 'Milestone',
  event_gift: 'Gift',
  event_logo: 'Logo',
  event_background: 'Background',
  event_collection_bg: 'Collection BG',
};

const typeColor: Record<string, string> = {
  event_thumbnail: 'bg-blue-500/20 text-blue-400',
  event_badge: 'bg-purple-500/20 text-purple-400',
  event_milestone: 'bg-emerald-500/20 text-emerald-400',
  event_gift: 'bg-[#FF9C15]/20 text-[#FF9C15]',
  event_logo: 'bg-cyan-500/20 text-cyan-400',
  event_background: 'bg-indigo-500/20 text-indigo-400',
  event_collection_bg: 'bg-pink-500/20 text-pink-400',
};

const AssetCard: React.FC<{
  product: WalletProduct;
  onReplace: (product: WalletProduct, file: File) => void;
  onRarityUpdate: (product: WalletProduct, tier: string, rarityFile: File | null, editedName?: string, editedPrice?: number, newAnimFile?: File | null, newStaticImageFile?: File | null, newWebmFile?: File | null, newMovFile?: File | null) => void;
  onDelete: (product: WalletProduct) => void;
  onMilestoneRewardSaved: () => void;
  replacing: boolean;
  deleting: boolean;
  cacheBust: number;
}> = ({ product, onReplace, onRarityUpdate, onDelete, onMilestoneRewardSaved, replacing, deleting, cacheBust }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const rarityFileRef = useRef<HTMLInputElement>(null);
  const animFileRef = useRef<HTMLInputElement>(null);
  const staticImageFileRef = useRef<HTMLInputElement>(null);
  const imageAsset = product.MediaAssets?.find((m) => m.Type === 'image');
  const animAsset = product.MediaAssets?.find((m) => m.Type === 'animation');
  const rarityAsset = product.MediaAssets?.find((m) => m.Type === 'rarity');
  const isGift = product.Type === 'event_gift';
  const isMilestone = product.Type === 'event_milestone';
  const currentTier = (product as any).Tags?.[0] || '';
  const [showEdit, setShowEdit] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [editTier, setEditTier] = useState(currentTier || 'rare');
  const [editName, setEditName] = useState(product.Name);
  const [editPrice, setEditPrice] = useState(String((product as any).PriceTokens || 0));
  const [newRarityFile, setNewRarityFile] = useState<File | null>(null);
  const [newRarityPreview, setNewRarityPreview] = useState<string | null>(null);
  const [newAnimFile, setNewAnimFile] = useState<File | null>(null);
  const [newAnimData, setNewAnimData] = useState<any | null>(null);
  const [newStaticImageFile, setNewStaticImageFile] = useState<File | null>(null);
  const [newStaticImagePreview, setNewStaticImagePreview] = useState<string | null>(null);
  const [newWebmFile, setNewWebmFile] = useState<File | null>(null);
  const [newMovFile, setNewMovFile] = useState<File | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const tierConfig = RARITY_TIERS.find(t => t.value === currentTier);

  const handleRarityFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setNewRarityFile(f);
    setNewRarityPreview(URL.createObjectURL(f));
  };

  const handleAnimFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    try {
      const data = await readLottie(f);
      setNewAnimFile(f);
      setNewAnimData(data);
    } catch { toast.error('Invalid Lottie JSON'); }
  };

  const handleStaticImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setNewStaticImageFile(f);
    setNewStaticImagePreview(URL.createObjectURL(f));
  };

  const handleWebmFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setNewWebmFile(f);
  };

  const handleMovFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    setNewMovFile(f);
  };

  const handleSave = () => {
    onRarityUpdate(product, editTier, newRarityFile, editName.trim() || product.Name, Number(editPrice) || 0, newAnimFile, newStaticImageFile, newWebmFile, newMovFile);
    setShowEdit(false);
    setNewRarityFile(null);
    setNewRarityPreview(null);
    setNewAnimFile(null);
    setNewAnimData(null);
    setNewStaticImageFile(null);
    setNewStaticImagePreview(null);
    setNewWebmFile(null);
    setNewMovFile(null);
  };

  return (
    <div className='bg-white/5 border border-white/10 rounded-xl p-3 space-y-2'>
      <div className='flex items-start gap-3'>
        <div className='shrink-0 w-16 h-16 rounded-lg bg-black/30 border border-white/10 overflow-hidden flex items-center justify-center'>
          {imageAsset ? (
            <img src={`${imageAsset.Url}?v=${cacheBust}`} alt={product.Name} className='w-full h-full object-contain' />
          ) : animAsset ? (
            <span className='text-[10px] text-gray-500 text-center px-1'>Lottie</span>
          ) : (
            <span className='text-[10px] text-gray-600'>—</span>
          )}
        </div>
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-semibold text-white truncate'>{product.Name}</p>
          <p className='text-xs font-mono text-gray-500 truncate'>{product.ProductKey}</p>
          <div className='flex items-center gap-2 mt-1 flex-wrap'>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${typeColor[product.Type] || 'bg-white/10 text-gray-400'}`}>
              {typeLabel[product.Type] || product.Type}
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${product.IsActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}`}>
              {product.IsActive ? 'Active' : 'Inactive'}
            </span>
            {isGift && tierConfig && (
              <span className='text-[10px] font-semibold px-1.5 py-0.5 rounded-full' style={{ backgroundColor: `${tierConfig.color}22`, color: tierConfig.color }}>
                {tierConfig.label}
              </span>
            )}
            {isGift && !tierConfig && (
              <span className='text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400'>No rarity</span>
            )}
            {isGift && (
              <button
                onClick={() => { setShowEdit((prev) => !prev); setConfirmDelete(false); setShowReward(false); }}
                disabled={replacing || deleting}
                className='text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition disabled:opacity-40'
              >
                {showEdit ? 'Cancel' : 'Edit'}
              </button>
            )}
            {isMilestone && (
              <button
                onClick={() => { setShowReward((prev) => !prev); setConfirmDelete(false); setShowEdit(false); }}
                disabled={replacing || deleting}
                className={`text-[10px] px-1.5 py-0.5 rounded-full transition disabled:opacity-40 ${showReward ? 'bg-[#FF9C15]/30 text-[#FF9C15]' : 'bg-[#FF9C15]/10 text-[#FF9C15] hover:bg-[#FF9C15]/20'}`}
              >
                {showReward ? 'Close Reward' : 'Reward ↑'}
              </button>
            )}
            {!isGift && !isMilestone && (
              <button
                onClick={() => fileRef.current?.click()}
                disabled={replacing || deleting}
                className='text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition disabled:opacity-40'
              >
                {replacing ? 'Replacing…' : 'Replace'}
              </button>
            )}
            {confirmDelete ? (
              <>
                <button
                  onClick={() => onDelete(product)}
                  disabled={deleting}
                  className='text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/80 text-white hover:bg-red-500 transition disabled:opacity-40'
                >
                  {deleting ? 'Deleting…' : 'Confirm'}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className='text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 hover:bg-white/20 transition disabled:opacity-40'
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => { setConfirmDelete(true); setShowEdit(false); }}
                disabled={replacing || deleting}
                className='text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition disabled:opacity-40'
              >
                Delete
              </button>
            )}
          </div>
          {isGift && rarityAsset && (
            <div className='flex items-center gap-1.5 mt-1.5'>
              <img src={rarityAsset.Url} alt='rarity badge' className='h-5 w-auto object-contain rounded' />
              <span className='text-[10px] text-gray-600'>Rarity badge</span>
            </div>
          )}
        </div>
      </div>

      {/* Full editor for gifts */}
      {isGift && showEdit && (
        <div className='bg-white/5 border border-white/10 rounded-lg p-3 space-y-3'>
          <p className='text-xs text-gray-400 font-semibold'>Edit Gift</p>

          <div className='grid grid-cols-2 gap-2'>
            <div>
              <label className='block text-[10px] text-gray-500 mb-1'>Name</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className='w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
              />
            </div>
            <div>
              <label className='block text-[10px] text-gray-500 mb-1'>Price (Tokens)</label>
              <input
                type='number'
                min='0'
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                className='w-full px-2 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-xs'
              />
            </div>
          </div>

          <div>
            <label className='block text-[10px] text-gray-500 mb-1'>Rarity Tier</label>
            <div className='flex gap-2 flex-wrap'>
              {RARITY_TIERS.map((t) => (
                <button
                  key={t.value}
                  type='button'
                  onClick={() => setEditTier(t.value)}
                  className={`px-3 py-1 text-xs font-semibold rounded-lg border transition ${
                    editTier === t.value ? 'text-black' : 'bg-white/5 border-white/20 text-gray-400 hover:border-white/40'
                  }`}
                  style={editTier === t.value ? { backgroundColor: t.color, borderColor: t.color } : {}}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className='grid grid-cols-5 gap-3'>
            <div>
              <p className='text-[10px] text-gray-500 mb-1'>Replace animation (.json)</p>
              <label className='cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block'>
                {newAnimFile ? newAnimFile.name : 'Select .json'}
                <input ref={animFileRef} type='file' accept='.json' className='hidden' onChange={handleAnimFileChange} />
              </label>
              {newAnimData && (
                <div className='mt-1 rounded-lg overflow-hidden bg-black/30 border border-white/10 flex items-center justify-center' style={{ height: 60 }}>
                  <LottiePreview data={newAnimData} />
                </div>
              )}
            </div>
            <div>
              <p className='text-[10px] text-gray-500 mb-1'>WebM animation (.webm)</p>
              <label className='cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block'>
                {newWebmFile ? newWebmFile.name : product.MediaAssets?.find(m => m.Type === 'webm') ? 'Replace .webm' : 'Select .webm'}
                <input type='file' accept='video/webm,.webm' className='hidden' onChange={handleWebmFileChange} />
              </label>
              {newWebmFile && <p className='text-[10px] text-emerald-400 mt-1'>{newWebmFile.name}</p>}
            </div>
            <div>
              <p className='text-[10px] text-gray-500 mb-1'>iOS alpha (.mov/.mp4)</p>
              <label className='cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block'>
                {newMovFile ? newMovFile.name : product.MediaAssets?.find(m => m.Type === 'ios_alpha') ? 'Replace .mov' : 'Select .mov'}
                <input type='file' accept='video/quicktime,video/mp4,video/x-m4v,.mov,.mp4,.m4v' className='hidden' onChange={handleMovFileChange} />
              </label>
              {newMovFile && <p className='text-[10px] text-emerald-400 mt-1'>{newMovFile.name}</p>}
            </div>
            <div>
              <p className='text-[10px] text-gray-500 mb-1'>Static image (PNG/JPG)</p>
              <label className='cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block'>
                {newStaticImageFile ? newStaticImageFile.name : imageAsset ? 'Replace image' : 'Select image'}
                <input ref={staticImageFileRef} type='file' accept='image/png,image/jpeg,image/webp' className='hidden' onChange={handleStaticImageChange} />
              </label>
              {(newStaticImagePreview || imageAsset) && (
                <img
                  src={newStaticImagePreview || `${imageAsset?.Url}?v=${cacheBust}`}
                  alt='static preview'
                  className='mt-1 h-10 w-auto object-contain rounded border border-white/10'
                />
              )}
            </div>
            <div>
              <p className='text-[10px] text-gray-500 mb-1'>Replace rarity badge</p>
              <label className='cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition inline-block'>
                {newRarityFile ? 'Replace' : 'Select image'}
                <input ref={rarityFileRef} type='file' accept='image/png,image/jpeg,image/webp,image/gif' className='hidden' onChange={handleRarityFileChange} />
              </label>
              {(newRarityPreview || rarityAsset) && (
                <img
                  src={newRarityPreview || rarityAsset?.Url}
                  alt='rarity badge'
                  className='mt-1 h-8 w-auto object-contain rounded border border-white/10'
                />
              )}
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={replacing}
            className='px-4 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
          >
            {replacing ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* Milestone reward asset uploader */}
      {isMilestone && showReward && (
        <MilestoneRewardSection
          product={product}
          onSaved={onMilestoneRewardSaved}
          disabled={replacing || deleting}
        />
      )}

      {!isGift && !isMilestone && (
        <input
          ref={fileRef}
          type='file'
          accept='image/png,image/jpeg,image/webp'
          className='hidden'
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) onReplace(product, f);
          }}
        />
      )}
    </div>
  );
};

// ── Main page ───────────────────────────────────────────────────────────────

const ASSET_TABS: { key: AssetTab; label: string; productType: string; description: string }[] = [
  { key: 'thumbnail', label: 'Thumbnail', productType: 'event_thumbnail', description: 'Main image shown on the event announcement card.' },
  { key: 'logo', label: 'Event Logo', productType: 'event_logo', description: 'Logo shown in the event page header.' },
  { key: 'background', label: 'Background', productType: 'event_background', description: 'Background image for the event announcement page.' },
  { key: 'collection_bg', label: 'Collection BG', productType: 'event_collection_bg', description: 'Background image for the event collection/shop page.' },
  { key: 'badge', label: 'Reward Badge', productType: 'event_badge', description: 'Badge awarded to participants on completion.' },
  { key: 'milestone', label: 'Milestones', productType: 'event_milestone', description: 'Milestone reward images shown at each level.' },
  { key: 'gift', label: 'Event Gifts', productType: 'event_gift', description: 'Lottie gift animations users can send during the event.' },
];

const ManageAnnouncementAssets: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [announcement, setAnnouncement] = useState<AnnouncementDetail | null>(null);
  const [products, setProducts] = useState<WalletProduct[]>([]);
  const [loadingAnn, setLoadingAnn] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [activeTab, setActiveTab] = useState<AssetTab>('thumbnail');
  const [showAddForm, setShowAddForm] = useState(false);
  const [replacingProductKey, setReplacingProductKey] = useState<string | null>(null);
  const [deletingProductKey, setDeletingProductKey] = useState<string | null>(null);
  const [imgBust, setImgBust] = useState(0);

  // Poster image (announcement-level, used for landing popup)
  const [posterFile, setPosterFile] = useState<File | null>(null);
  const [posterPreview, setPosterPreview] = useState<string | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [savingPoster, setSavingPoster] = useState(false);

  // Storyboard video (announcement-level, played fullscreen from the event page hero banner).
  // Transcoded into multiple resolutions by tezda-video-processing after upload — status is
  // polled until the job lands on 'ready' or 'failed'.
  const [storyboardVideoFile, setStoryboardVideoFile] = useState<File | null>(null);
  const [storyboardVideoPreview, setStoryboardVideoPreview] = useState<string | null>(null);
  const [storyboardVideo, setStoryboardVideo] = useState<AnnouncementDetail['storyboardVideo']>(null);
  const [savingStoryboardVideo, setSavingStoryboardVideo] = useState(false);

  const fetchAnnouncement = useCallback(async () => {
    if (!id) return;
    setLoadingAnn(true);
    try {
      const res = await fetch(`${MARKETING_API_BASE_URL}/announcements?limit=200&offset=0`, {
        headers: getMarketingApiHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const list: AnnouncementDetail[] = json.data?.announcements || json.announcements || (Array.isArray(json.data) ? json.data : []);
      const found = list.find((a) => a._id === id);
      if (!found) throw new Error('Announcement not found');
      setAnnouncement(found);
    } catch (err) {
      toast.error('Failed to load announcement: ' + (err as Error).message);
    } finally {
      setLoadingAnn(false);
    }
  }, [id]);

  const handleSavePoster = useCallback(async () => {
    if (!posterFile || !id) return;
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    setSavingPoster(true);
    try {
      const url = await presignAndUpload(id, 'announcement_poster', posterFile, posterFile.type || 'image/jpeg', getApiHeaders(token));
      const res = await fetch(`${MARKETING_API_BASE_URL}/announcements/${id}`, {
        method: 'PUT',
        headers: { ...getMarketingApiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ posterImageUrl: url }),
      });
      if (!res.ok) throw new Error('Failed to save poster');
      setPosterUrl(url);
      setPosterFile(null);
      setPosterPreview(null);
      toast.success('Poster image saved!');
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    } finally {
      setSavingPoster(false);
    }
  }, [posterFile, id]);

  const handleSaveStoryboardVideo = useCallback(async () => {
    if (!storyboardVideoFile || !id) return;
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    setSavingStoryboardVideo(true);
    try {
      const sourceUrl = await presignAndUpload(id, 'announcement_storyboard_video', storyboardVideoFile, storyboardVideoFile.type || 'video/mp4', getApiHeaders(token));
      const res = await fetch(`${MARKETING_API_BASE_URL}/announcements/${id}/storyboard-video`, {
        method: 'POST',
        headers: { ...getMarketingApiHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceUrl }),
      });
      if (!res.ok) throw new Error('Failed to start storyboard video processing');
      setStoryboardVideo({ status: 'processing', sourceUrl, resolutions: [] });
      setStoryboardVideoFile(null);
      setStoryboardVideoPreview(null);
      toast.success('Storyboard video uploaded — processing into multiple resolutions…');
    } catch (err) {
      toast.error((err as Error).message || 'Upload failed');
    } finally {
      setSavingStoryboardVideo(false);
    }
  }, [storyboardVideoFile, id]);

  const fetchProducts = useCallback(async (categoryKey: string) => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) return;
    setLoadingProducts(true);
    try {
      const res = await fetch(`${walletApiBase}/getWalletShopProductsV2`, {
        method: 'POST',
        headers: getApiHeaders(token),
        body: JSON.stringify({ UserID: 'dashboard', CategoryKey: categoryKey, CurrencyType: 'token', IncludeInactive: true }),
      });
      const json = await res.json();
      setProducts(json?.data?.products || []);
    } catch (err) {
      toast.error('Failed to load products: ' + (err as Error).message);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  useEffect(() => {
    if (announcement) {
      setPosterUrl(announcement.posterImageUrl || null);
      setStoryboardVideo(announcement.storyboardVideo || null);
    }
  }, [announcement]);

  useEffect(() => {
    if (announcement?.eventConfig?.categoryKey) {
      fetchProducts(announcement.eventConfig.categoryKey);
    }
  }, [announcement, fetchProducts]);

  // Poll while the transcode job is in flight — stops itself once the
  // announcement's storyboardVideo lands on 'ready' or 'failed'.
  useEffect(() => {
    if (storyboardVideo?.status !== 'processing' || !id) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${MARKETING_API_BASE_URL}/announcements?limit=200&offset=0`, {
          headers: getMarketingApiHeaders(),
        });
        if (!res.ok) return;
        const json = await res.json();
        const list: AnnouncementDetail[] = json.data?.announcements || json.announcements || (Array.isArray(json.data) ? json.data : []);
        const found = list.find((a) => a._id === id);
        if (found?.storyboardVideo) {
          setStoryboardVideo(found.storyboardVideo);
          if (found.storyboardVideo.status === 'ready') {
            toast.success('Storyboard video ready!');
          } else if (found.storyboardVideo.status === 'failed') {
            toast.error(`Storyboard video processing failed: ${found.storyboardVideo.error || 'unknown error'}`);
          }
        }
      } catch {
        // transient — next poll will retry
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [storyboardVideo?.status, id]);

  const handleAssetCreated = useCallback(async (assetUrl?: string) => {
    setShowAddForm(false);
    if (announcement?.eventConfig?.categoryKey) {
      fetchProducts(announcement.eventConfig.categoryKey);
    }
    // Auto-sync thumbnailUrl on announcement when logo or thumbnail asset is uploaded
    if (assetUrl && id && (activeTab === 'logo' || activeTab === 'thumbnail')) {
      try {
        await fetch(`${MARKETING_API_BASE_URL}/announcements/${id}`, {
          method: 'PUT',
          headers: { ...getMarketingApiHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ thumbnailUrl: assetUrl }),
        });
        toast.success('Announcement thumbnail synced!');
      } catch {
        // non-critical — asset still created
      }
    }
  }, [announcement, fetchProducts, activeTab, id]);

  const handleReplace = useCallback(async (product: WalletProduct, file: File) => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    const headers = getApiHeaders(token);
    setReplacingProductKey(product.ProductKey);
    try {
      const contentType = file.type || 'image/png';
      const imageUrl = await presignAndUpload(product.ProductKey, 'image', file, contentType, headers);
      const res = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: product.ProductKey,
          ...requiredProductFields(product),
          MediaAssets: [{ Type: 'image', Url: imageUrl, MimeType: contentType }],
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Update failed');
      toast.success('Image replaced!');
      if (announcement?.eventConfig?.categoryKey) fetchProducts(announcement.eventConfig.categoryKey);
    } catch (err) {
      toast.error((err as Error).message || 'Replace failed');
    } finally {
      setReplacingProductKey(null);
    }
  }, [announcement, fetchProducts]);

  const handleRarityUpdate = useCallback(async (
    product: WalletProduct,
    tier: string,
    rarityFile: File | null,
    editedName?: string,
    editedPrice?: number,
    newAnimFile?: File | null,
    newStaticImageFile?: File | null,
    newWebmFile?: File | null,
    newMovFile?: File | null,
  ) => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    const headers = getApiHeaders(token);
    setReplacingProductKey(product.ProductKey);
    try {
      const updateBody: Record<string, unknown> = {
        ProductKey: product.ProductKey,
        ...requiredProductFields(product),
        Tags: [tier],
      };
      if (editedName) updateBody.Name = editedName;
      if (editedPrice !== undefined) updateBody.PriceTokens = editedPrice;

      let assets = [...(product.MediaAssets || [])];
      if (newAnimFile) {
        const animUrl = await presignAndUpload(product.ProductKey, 'animation', newAnimFile, 'application/json', headers);
        assets = assets.filter((m) => m.Type !== 'animation');
        assets.push({ Type: 'animation', Url: animUrl, MimeType: 'application/json' });
      }
      if (rarityFile) {
        const contentType = rarityFile.type || 'image/png';
        const rarityUrl = await presignAndUpload(product.ProductKey, 'rarity', rarityFile, contentType, headers);
        assets = assets.filter((m) => m.Type !== 'rarity');
        assets.push({ Type: 'rarity', Url: rarityUrl, MimeType: contentType });
      }
      if (newStaticImageFile) {
        const contentType = newStaticImageFile.type || 'image/png';
        const imageUrl = await presignAndUpload(product.ProductKey, 'image', newStaticImageFile, contentType, headers);
        assets = assets.filter((m) => m.Type !== 'image');
        assets.push({ Type: 'image', Url: imageUrl, MimeType: contentType });
      }
      if (newWebmFile) {
        const webmUrl = await presignAndUpload(product.ProductKey, 'webm', newWebmFile, 'video/webm', headers);
        assets = assets.filter((m) => m.Type !== 'webm');
        assets.push({ Type: 'webm', Url: webmUrl, MimeType: 'video/webm' });
      }
      if (newMovFile) {
        const movContentType = newMovFile.type || 'video/quicktime';
        const movUrl = await presignAndUpload(product.ProductKey, 'ios_alpha', newMovFile, movContentType, headers);
        assets = assets.filter((m) => m.Type !== 'ios_alpha');
        assets.push({ Type: 'ios_alpha', Url: movUrl, MimeType: movContentType });
      }
      if (newAnimFile || rarityFile || newStaticImageFile || newWebmFile || newMovFile) updateBody.MediaAssets = assets;

      const res = await fetch(`${walletApiBase}/updateWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify(updateBody),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Update failed');
      toast.success('Gift updated!');
      setImgBust((b) => b + 1);
      if (announcement?.eventConfig?.categoryKey) fetchProducts(announcement.eventConfig.categoryKey);
    } catch (err) {
      toast.error((err as Error).message || 'Update failed');
    } finally {
      setReplacingProductKey(null);
    }
  }, [announcement, fetchProducts]);

  const handleDelete = useCallback(async (product: WalletProduct) => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) { toast.error('Missing auth token or API key.'); return; }
    setDeletingProductKey(product.ProductKey);
    try {
      const res = await fetch(`${walletApiBase}/deleteWalletShopProduct`, {
        method: 'POST',
        headers: getApiHeaders(token),
        body: JSON.stringify({ ProductKey: product.ProductKey }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Delete failed');
      toast.success(`"${product.Name}" deleted.`);
      if (announcement?.eventConfig?.categoryKey) fetchProducts(announcement.eventConfig.categoryKey);
    } catch (err) {
      toast.error((err as Error).message || 'Delete failed');
    } finally {
      setDeletingProductKey(null);
    }
  }, [announcement, fetchProducts]);

  const categoryKey = announcement?.eventConfig?.categoryKey || '';
  const activeTabConfig = ASSET_TABS.find((t) => t.key === activeTab)!;
  const tabProducts = products.filter((p) => p.Type === activeTabConfig.productType);

  if (loadingAnn) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='flex items-center justify-center py-32'>
          <div className='w-8 h-8 rounded-full border-2 border-white/20 border-t-[#FF9C15] animate-spin' />
        </div>
      </div>
    );
  }

  if (!announcement) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='max-w-4xl mx-auto px-4 py-20 text-center text-gray-400'>Announcement not found.</div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4'>
          <div>
            <button
              onClick={() => navigate('/announcements')}
              className='text-xs text-gray-500 hover:text-gray-300 mb-2 flex items-center gap-1'
            >
              ← Back to announcements
            </button>
            <h1 className='text-2xl font-bold text-white'>{announcement.title}</h1>
            <div className='flex items-center gap-2 mt-2'>
              {categoryKey ? (
                <span className='font-mono text-xs px-2 py-1 bg-[#FF9C15]/10 text-[#FF9C15] rounded'>
                  {categoryKey}
                </span>
              ) : (
                <span className='text-xs text-yellow-400 bg-yellow-400/10 px-2 py-1 rounded'>
                  No categoryKey — set Event Config in tezda-report first
                </span>
              )}
              <span className='text-xs text-gray-500 capitalize'>{announcement.status}</span>
            </div>
          </div>
        </div>

        {/* Poster Image — announcement-level, shown on landing popup */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
          <div>
            <h2 className='text-lg font-semibold text-white'>Poster Image</h2>
            <p className='text-sm text-gray-400 mt-0.5'>Full-screen image shown on the landing popup when users open the app. Managed per announcement.</p>
          </div>
          {posterUrl && !posterFile && (
            <img src={posterUrl} alt='Poster' className='h-40 w-auto rounded-lg border border-white/10 object-contain' />
          )}
          {posterPreview && (
            <img src={posterPreview} alt='Preview' className='h-40 w-auto rounded-lg border border-white/10 object-contain' />
          )}
          <div className='flex items-center gap-3'>
            <label className={`cursor-pointer px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition ${savingPoster ? 'opacity-50 pointer-events-none' : ''}`}>
              {posterFile ? posterFile.name.slice(0, 24) : posterUrl ? 'Replace Poster' : 'Select Poster'}
              <input type='file' accept='image/png,image/jpeg,image/webp' className='hidden' disabled={savingPoster} onChange={e => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                setPosterFile(f);
                setPosterPreview(URL.createObjectURL(f));
              }} />
            </label>
            {posterFile && (
              <button
                onClick={handleSavePoster}
                disabled={savingPoster}
                className='px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
              >
                {savingPoster ? 'Uploading…' : 'Save Poster'}
              </button>
            )}
            {posterFile && (
              <button onClick={() => { setPosterFile(null); setPosterPreview(null); }} disabled={savingPoster}
                className='px-3 py-2 text-sm text-gray-400 hover:text-white transition disabled:opacity-40'>
                Cancel
              </button>
            )}
          </div>
        </div>

        {/* Storyboard Video — announcement-level, played fullscreen from the event page hero banner */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
          <div>
            <h2 className='text-lg font-semibold text-white'>Storyboard Video</h2>
            <p className='text-sm text-gray-400 mt-0.5'>Plays fullscreen when the play button on the event page's hero banner is tapped. Optional — the hero banner shows as a static image when unset.</p>
          </div>
          {storyboardVideo?.status === 'processing' && !storyboardVideoFile && (
            <div className='flex items-center gap-2 text-sm text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2 w-fit'>
              <span className='inline-block h-3 w-3 rounded-full border-2 border-yellow-300 border-t-transparent animate-spin' />
              Processing into multiple resolutions…
            </div>
          )}
          {storyboardVideo?.status === 'failed' && !storyboardVideoFile && (
            <div className='text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2'>
              Processing failed: {storyboardVideo.error || 'unknown error'}. Select a video to retry.
            </div>
          )}
          {storyboardVideo?.status === 'ready' && storyboardVideo.resolutions.length > 0 && !storyboardVideoFile && (
            <video src={storyboardVideo.resolutions[0]} controls className='h-40 w-auto rounded-lg border border-white/10' />
          )}
          {storyboardVideoPreview && (
            <video src={storyboardVideoPreview} controls className='h-40 w-auto rounded-lg border border-white/10' />
          )}
          <div className='flex items-center gap-3'>
            <label className={`cursor-pointer px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition ${savingStoryboardVideo ? 'opacity-50 pointer-events-none' : ''}`}>
              {storyboardVideoFile ? storyboardVideoFile.name.slice(0, 24) : storyboardVideo ? 'Replace Video' : 'Select Video'}
              <input type='file' accept='video/mp4,video/webm,video/quicktime' className='hidden' disabled={savingStoryboardVideo} onChange={e => {
                const f = e.target.files?.[0]; e.target.value = '';
                if (!f) return;
                setStoryboardVideoFile(f);
                setStoryboardVideoPreview(URL.createObjectURL(f));
              }} />
            </label>
            {storyboardVideoFile && (
              <button
                onClick={handleSaveStoryboardVideo}
                disabled={savingStoryboardVideo}
                className='px-4 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
              >
                {savingStoryboardVideo ? 'Uploading…' : 'Save Video'}
              </button>
            )}
            {storyboardVideoFile && (
              <button onClick={() => { setStoryboardVideoFile(null); setStoryboardVideoPreview(null); }} disabled={savingStoryboardVideo}
                className='px-3 py-2 text-sm text-gray-400 hover:text-white transition disabled:opacity-40'>
                Cancel
              </button>
            )}
          </div>
        </div>

        {!categoryKey && (
          <div className='bg-yellow-500/10 border border-yellow-500/30 rounded-xl px-4 py-3 text-sm text-yellow-300'>
            This announcement does not have an Event Config categoryKey. Go to tezda-report → Edit Announcement → Event Config tab to set one.
          </div>
        )}

        {categoryKey && (
          <>
            {/* Asset type tabs */}
            <div className='flex items-center gap-2 flex-wrap'>
              {ASSET_TABS.map((tab) => {
                const count = products.filter((p) => p.Type === tab.productType).length;
                return (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveTab(tab.key); setShowAddForm(false); }}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition flex items-center gap-2 ${
                      activeTab === tab.key
                        ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20'
                    }`}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span className='px-1.5 py-0.5 text-[10px] rounded-full bg-white/20'>{count}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-5'>
              <div className='flex items-start justify-between gap-4'>
                <div>
                  <h2 className='text-lg font-semibold text-white'>{activeTabConfig.label}</h2>
                  <p className='text-sm text-gray-400 mt-0.5'>{activeTabConfig.description}</p>
                </div>
                <button
                  onClick={() => setShowAddForm((prev) => !prev)}
                  className='shrink-0 px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
                >
                  {showAddForm ? 'Cancel' : '+ Add'}
                </button>
              </div>

              {/* Add form */}
              {showAddForm && (
                <div className='bg-white/5 border border-white/10 rounded-xl p-4'>
                  <p className='text-xs text-gray-500 mb-3'>
                    CategoryKey: <span className='font-mono text-[#FF9C15]'>{categoryKey}</span>
                  </p>
                  {activeTab === 'gift' ? (
                    <GiftAssetForm categoryKey={categoryKey} onCreated={handleAssetCreated} />
                  ) : (
                    <ImageAssetForm
                      categoryKey={categoryKey}
                      productType={activeTabConfig.productType}
                      label={activeTabConfig.label}
                      placeholderKey={`${categoryKey}_${activeTabConfig.productType.replace('event_', '').toUpperCase()}`}
                      onCreated={handleAssetCreated}
                    />
                  )}
                </div>
              )}

              {/* Existing assets */}
              {loadingProducts ? (
                <div className='flex items-center justify-center py-8'>
                  <div className='w-6 h-6 rounded-full border-2 border-white/20 border-t-[#FF9C15] animate-spin' />
                </div>
              ) : tabProducts.length === 0 ? (
                <p className='text-sm text-gray-600 py-4 text-center'>No {activeTabConfig.label.toLowerCase()} assets yet for this announcement.</p>
              ) : (
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {tabProducts.map((p) => (
                    <AssetCard key={p.ProductID} product={p} onReplace={handleReplace} onRarityUpdate={handleRarityUpdate} onDelete={handleDelete} onMilestoneRewardSaved={() => { if (announcement?.eventConfig?.categoryKey) fetchProducts(announcement.eventConfig.categoryKey); }} replacing={replacingProductKey === p.ProductKey} deleting={deletingProductKey === p.ProductKey} cacheBust={imgBust} />
                  ))}
                </div>
              )}
            </div>

            {/* Summary of all assets */}
            {products.length > 0 && (
              <div className='bg-white/5 border border-white/10 rounded-2xl px-6 py-4'>
                <p className='text-xs text-gray-400 font-semibold mb-2'>All assets for <span className='font-mono text-[#FF9C15]'>{categoryKey}</span></p>
                <div className='flex items-center gap-4 flex-wrap'>
                  {ASSET_TABS.map((tab) => {
                    const count = products.filter((p) => p.Type === tab.productType).length;
                    return (
                      <div key={tab.key} className='text-center'>
                        <p className='text-lg font-bold text-white'>{count}</p>
                        <p className='text-xs text-gray-500'>{tab.label}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ManageAnnouncementAssets;
